import React, { useEffect, useState } from "react";
import { CheckCircle2, ArrowLeft, Sparkles, ClipboardList, Bot, ArrowRight, AlertTriangle, LogOut, Loader2 } from "lucide-react";
import {
  QUIZ_QUESTIONS, SCALE_OPTIONS, MOTIVATION_RESULTS,
  RESULT_WARNING, RESULT_CTA_TEXT, computeQuizResult,
} from "@/lib/extraQuizData";
import { extraApi, extraAuthHeaders, getExtraToken, setExtraToken } from "./extra/extraShared";
import ExtraAuth from "./extra/ExtraAuth";
import ExtraChecklist from "./extra/ExtraChecklist";
import ExtraAiGuide from "./extra/ExtraAiGuide";

const GOLD = "#C9A24B";

function Eyebrow() {
  return (
    <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: GOLD }} data-testid="extra-eyebrow">
      <span className="w-6 h-px" style={{ backgroundColor: GOLD }} />
      Prime Vision Extra
    </div>
  );
}

function BackBar({ onBack, label = "Indietro" }) {
  return (
    <button onClick={onBack} data-testid="extra-back" className="inline-flex items-center gap-1.5 text-xs font-bold text-white/60 hover:text-white transition-colors mb-5">
      <ArrowLeft size={15} /> {label}
    </button>
  );
}

function HomeScreen({ onOpen, user }) {
  const cards = [
    { id: "quiz", kind: "Quiz", icon: Sparkles, accent: GOLD, title: "Profilo motivazionale", desc: "12 domande, risultato personalizzato e indicazioni su cosa fare, cosa dire e come farti percepire.", cta: "Inizia il quiz" },
    { id: "checklist", kind: "Checklist", icon: ClipboardList, accent: "#10B981", title: "Checklist anti No Show", desc: "Schema operativo per controllare reminder, conferme, spostamenti e recupero slot.", cta: "Visualizza checklist" },
    { id: "ai", kind: "Guida", icon: Bot, accent: "#F43F5E", title: "AI in studio medico", desc: "Materiali pratici per distinguere automazioni, AI generativa, dati e supervisione umana.", cta: "Visualizza guida" },
  ];
  return (
    <div className="animate-[fade-in-up_.5s_ease_both]">
      <Eyebrow />
      <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold leading-tight" data-testid="extra-title">
        Contenuti Extra non solo per Medici Oculisti
      </h1>
      <p className="mt-4 text-base text-white/70 leading-relaxed" data-testid="extra-intro-text">
        Quiz, checklist e strumenti operativi per aiutare studi medici, professionisti sanitari e team di segreteria a ridurre i No Show, comunicare meglio con i pazienti e organizzare il lavoro con più chiarezza.
      </p>
      {!user && (
        <p className="mt-3 text-xs text-white/45">Per accedere ai contenuti basta un account gratuito: lo crei una volta sola.</p>
      )}
      <div className="mt-8 grid gap-4">
        {cards.map((c) => (
          <div key={c.id} className="bg-white rounded-lg p-5 sm:p-6 shadow-xl" data-testid={`extra-card-${c.id}`}>
            <div className="flex items-center gap-2">
              <c.icon size={16} style={{ color: c.accent }} className="shrink-0" />
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: c.accent }}>{c.kind}</span>
            </div>
            <h2 className="mt-2 text-lg font-extrabold text-brand-navy">{c.title}</h2>
            <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
            <button
              onClick={() => onOpen(c.id)}
              data-testid={`extra-open-${c.id}`}
              className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-5 py-2.5 rounded-lg text-sm transition-colors"
            >
              <c.icon size={15} /> {c.cta}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuizScreen({ onDone, onBack }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const q = QUIZ_QUESTIONS[idx];
  const pick = (val) => {
    const next = { ...answers, [q.id]: val };
    setAnswers(next);
    setTimeout(() => {
      if (idx < QUIZ_QUESTIONS.length - 1) setIdx(idx + 1);
      else onDone(next);
    }, 180);
  };
  return (
    <div className="animate-[fade-in-up_.4s_ease_both]">
      <BackBar onBack={() => (idx === 0 ? onBack() : setIdx(idx - 1))} />
      <div className="flex items-center justify-between text-xs text-white/50">
        <span className="font-bold" style={{ color: GOLD }}>Profilo motivazionale</span>
        <span data-testid="quiz-progress-label">Domanda {idx + 1} di {QUIZ_QUESTIONS.length}</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full bg-brand-azure transition-all duration-300" style={{ width: `${((idx + 1) / QUIZ_QUESTIONS.length) * 100}%` }} />
      </div>
      <div className="mt-6 bg-white rounded-lg p-6 sm:p-8 text-slate-800 shadow-xl">
        <h2 className="text-lg sm:text-xl font-extrabold text-brand-navy leading-snug" data-testid="quiz-question-text">{q.text}</h2>
        <p className="mt-1 text-xs text-slate-400">Rispondi pensando a te stesso: quanto ti riconosci in questa frase?</p>
        <div className="mt-5 grid gap-2">
          {SCALE_OPTIONS.map((o) => {
            const active = answers[q.id] === o.value;
            return (
              <button
                key={o.value}
                onClick={() => pick(o.value)}
                data-testid={`quiz-answer-${o.value}`}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border text-sm font-bold transition-colors ${active ? "bg-brand-navy text-white border-brand-navy" : "bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200"}`}
              >
                {o.label}
                <span className={`text-xs ${active ? "text-white/70" : "text-slate-400"}`}>{o.value}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ResultScreen({ result, onBack }) {
  const dom = MOTIVATION_RESULTS[result.dominant];
  const sec = MOTIVATION_RESULTS[result.second];
  const blocks = [
    { title: "Cosa fare", text: dom.cosaFare },
    { title: "Cosa dire", text: dom.cosaDire, quote: true },
    { title: "Come farti percepire", text: dom.percezione },
  ];
  return (
    <div className="animate-[fade-in-up_.5s_ease_both]" data-testid="quiz-result">
      <BackBar onBack={onBack} label="Torna ai contenuti" />
      <Eyebrow />
      <div className="mt-3 text-xs font-bold uppercase tracking-widest text-white/50">La tua motivazione primaria</div>
      <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold" style={{ color: GOLD }} data-testid="result-dominant">{dom.label}</h1>
      <p className="mt-3 text-sm sm:text-base text-white/75 leading-relaxed">{dom.intro}</p>
      <div className="mt-6 grid gap-3">
        {blocks.map((b) => (
          <div key={b.title} className="bg-white rounded-lg p-5 sm:p-6 shadow-xl">
            <div className="text-[11px] font-bold uppercase tracking-widest text-brand-azure">{b.title}</div>
            <p className={`mt-2 text-sm leading-relaxed ${b.quote ? "italic text-brand-navy border-l-2 pl-3" : "text-slate-600"}`} style={b.quote ? { borderColor: GOLD } : undefined}>
              {b.quote ? `“${b.text}”` : b.text}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-lg border border-white/15 bg-white/5 p-5" data-testid="result-second">
        <div className="text-[11px] font-bold uppercase tracking-widest text-white/50">La tua motivazione secondaria</div>
        <div className="mt-1 text-lg font-extrabold text-white">{sec.label}</div>
        <p className="mt-1.5 text-xs text-white/60 leading-relaxed">{sec.intro}</p>
      </div>
      <div className="mt-6 flex items-start gap-3 rounded-lg p-5 border" style={{ borderColor: `${GOLD}55`, backgroundColor: `${GOLD}14` }} data-testid="result-warning">
        <AlertTriangle size={18} className="shrink-0 mt-0.5" style={{ color: GOLD }} />
        <p className="text-sm text-white/85 leading-relaxed">{RESULT_WARNING}</p>
      </div>
      <div className="mt-8 bg-white rounded-lg p-6 sm:p-8 shadow-xl text-center">
        <CheckCircle2 size={22} className="mx-auto text-brand-azure" />
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">{RESULT_CTA_TEXT}</p>
        <a
          href="https://www.primevision.app"
          data-testid="result-cta"
          className="mt-5 inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-7 py-3 rounded-lg text-sm transition-colors"
        >
          Visita primevision.app <ArrowRight size={15} />
        </a>
      </div>
    </div>
  );
}

export default function Extra() {
  const [view, setView] = useState("home"); // home | auth | quiz | result | checklist | ai
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!getExtraToken());
  const [pending, setPending] = useState(null); // contenuto richiesto prima dell'auth
  const [result, setResult] = useState(null);

  useEffect(() => {
    const t = getExtraToken();
    if (!t) return;
    extraApi.get("/extra/auth/me", { headers: { Authorization: `Bearer ${t}` } })
      .then(({ data }) => setUser(data.user))
      .catch(() => setExtraToken(null))
      .finally(() => setChecking(false));
  }, []);

  const openContent = (id) => {
    if (!user) { setPending(id); setView("auth"); window.scrollTo(0, 0); return; }
    setView(id);
    window.scrollTo(0, 0);
  };

  const onAuthed = (token, u) => {
    setExtraToken(token);
    setUser(u);
    const target = pending || "home";
    setPending(null);
    setView(target);
    window.scrollTo(0, 0);
  };

  const logout = async () => {
    try { await extraApi.post("/extra/auth/logout", {}, { headers: extraAuthHeaders() }); } catch { /* stateless */ }
    setExtraToken(null);
    setUser(null);
    setView("home");
  };

  const onQuizDone = async (answers) => {
    const r = computeQuizResult(answers);
    setResult(r);
    setView("result");
    window.scrollTo(0, 0);
    try {
      await extraApi.post("/extra/quiz-result", { quizResult: r, answers }, { headers: extraAuthHeaders() });
    } catch { /* il risultato resta visibile anche se il salvataggio fallisce */ }
  };

  const goHome = () => { setView("home"); window.scrollTo(0, 0); };

  return (
    <div className="min-h-screen bg-brand-navyDark text-white">
      <div className="max-w-2xl mx-auto px-5 py-8 sm:py-12">
        <div className="mb-8 flex items-center justify-between gap-3">
          <a href="https://www.primevision.app" className="text-lg font-extrabold tracking-tight text-white/90" data-testid="extra-logo">
            Prime<span className="text-brand-azure">Vision</span>
          </a>
          {user && (
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-xs text-white/55 truncate" data-testid="extra-user-name">Ciao, {user.firstName}</span>
              <button onClick={logout} data-testid="extra-logout" className="inline-flex items-center gap-1 text-xs font-bold text-white/60 hover:text-white transition-colors shrink-0">
                <LogOut size={13} /> Esci
              </button>
            </div>
          )}
        </div>

        {checking ? (
          <div className="py-16 text-center text-white/50"><Loader2 className="animate-spin mx-auto" /></div>
        ) : (
          <>
            {view === "home" && <HomeScreen onOpen={openContent} user={user} />}
            {view === "auth" && (
              <div className="animate-[fade-in-up_.4s_ease_both]">
                <BackBar onBack={() => { setPending(null); goHome(); }} />
                <Eyebrow />
                <h1 className="mt-3 text-2xl font-extrabold leading-tight" data-testid="auth-title">Accedi o crea il tuo account</h1>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">
                  I contenuti extra sono riservati agli utenti registrati. Bastano pochi secondi: inserisci i dati una volta sola e accedi a tutti i contenuti.
                </p>
                <div className="mt-5">
                  <ExtraAuth onAuthed={onAuthed} />
                </div>
              </div>
            )}
            {view === "quiz" && <QuizScreen onBack={goHome} onDone={onQuizDone} />}
            {view === "result" && result && <ResultScreen result={result} onBack={goHome} />}
            {view === "checklist" && (
              <div className="animate-[fade-in-up_.4s_ease_both]">
                <BackBar onBack={goHome} />
                <ExtraChecklist />
              </div>
            )}
            {view === "ai" && (
              <div className="animate-[fade-in-up_.4s_ease_both]">
                <BackBar onBack={goHome} />
                <ExtraAiGuide />
              </div>
            )}
          </>
        )}

        <div className="mt-12 pt-6 border-t border-white/10 text-[11px] text-white/40 leading-relaxed">
          Contenuti extra Prime Vision per studi medici e team di segreteria. Nessun dato sanitario viene richiesto in questa pagina.
        </div>
      </div>
    </div>
  );
}
